import cn from 'classnames';
import debounce from 'lodash.debounce';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import {
  useCheckoutContext,
  Article,
  SetArticleQuantityBody,
  SetArticleQuantityBodyV2,
  Price,
} from '@zooplus/checkout-state';
import { QuantityStepper, QuantityPicker } from '@zooplus/ui-components';

import useCartTracking from '@hooks/use-adobe-tracking/use-cart-tracking';
import { useIsResponsiveViewport } from '@hooks/use-is-responsive-viewport';
import { useTranslation } from '@hooks/use-translations';
import {
  ADOBE_TRACKING,
  KEYBOARD_EVENT_CODE,
  CHECKOUT_STATE_PROJECT_ID,
  CART_VERSION,
  breakpointsNumeric,
} from '@utilities/constants';
import { isArrayNotEmpty, splitShopId } from '@utilities/utils';

import styles from './cart-item-quantity-handler.module.scss';

interface loaderStates {
  [key: number]: boolean;
}

const CartItemQuantityHandler = (article: Article) => {
  const {
    setArticleQuantity,
    isLoading: loadingIndicator,
    cartVersion,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { trackArticleInteractionEvent } = useCartTracking();
  const { CART_ADDITION, CART_REMOVAL } = ADOBE_TRACKING;
  const { t } = useTranslation();
  const firstRender = useRef(true);

  const [isLoading, setLoading] = useState<loaderStates>({});
  const [itemCount, setItemCount] = useState<number>(article.quantity);
  const isResponsiveViewport = useIsResponsiveViewport(breakpointsNumeric.breakpointMd);

  const handleLoader = useCallback(
    (value: boolean) => {
      setLoading((ids) => ({
        ...ids,
        [article.id]: value,
      }));
    },
    [article.id],
  );

  const handleInputQuantintyDeletion = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      article.quantity.toString().length === 1 &&
      [KEYBOARD_EVENT_CODE.BACKSPACE, KEYBOARD_EVENT_CODE.DELETE].includes(event.code)
    ) {
      setItemCount(0);
    }
  };

  useEffect(() => {
    !loadingIndicator && handleLoader(!!loadingIndicator);
  }, [loadingIndicator, handleLoader]);

  const handleChange = (value: number) => {
    if (!isNaN(value) && value !== itemCount) {
      setItemCount(value);
      handleLoader(true);
    }
  };

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    debouncedChangeHandler(itemCount, article.id);
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [itemCount, article.id]);

  const handleEffect = useCallback(
    (value: number, id: number) => {
      const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
      const newArticleQuantity =
        article.quantity > value ? article.quantity - value : value - article.quantity;
      const total = (
        (isCartApiV2 ? (article.price as Price)?.value : article.actualPrice) * newArticleQuantity
      ).toFixed(2);
      const [shopIdentifier, variantId] = splitShopId(article.shopId);
      const articleTrackingPayload: Pick<
        Article,
        'id' | 'price' | 'voucherApplied' | 'shipmentId'
      > = {
        id: article.id,
        price: isCartApiV2 ? (article.price as Price)?.value : article.price,
        voucherApplied: isCartApiV2
          ? article.lineItemDiscounts?.articleBasedDiscount?.applied
          : article.voucherApplied,
        shipmentId: article.shipmentId,
      };
      article.quantity > value
        ? trackArticleInteractionEvent(
            CART_REMOVAL,
            newArticleQuantity,
            total,
            shopIdentifier,
            variantId,
            {
              ...articleTrackingPayload,
            },
          )
        : trackArticleInteractionEvent(
            CART_ADDITION,
            newArticleQuantity,
            total,
            shopIdentifier,
            variantId,
            {
              ...articleTrackingPayload,
            },
          );

      const setArticleQuantityPayload: SetArticleQuantityBody = {
        articleId: id,
        quantity: value,
      };
      if (isCartApiV2 && article.offerId) {
        (setArticleQuantityPayload as SetArticleQuantityBodyV2).offerIds = [article.offerId];
      }
      if (value === 0) {
        setArticleQuantityPayload.articleName = article.name;
      }
      if (value !== 0 && !isCartApiV2) {
        setArticleQuantityPayload.subscriptionSelected = article.subscriptionSelected;
        setArticleQuantityPayload.voucherApplied = article.voucherApplied;
      }
      setArticleQuantity?.(setArticleQuantityPayload);
    },
    [article.quantity, itemCount],
  );

  const debouncedChangeHandler = debounce(handleEffect, 300);

  useEffect(() => {
    if (itemCount > article.maxQuantity) {
      setItemCount(article.quantity);
    }
  }, [itemCount, article.maxQuantity]);

  return (
    <div className={styles.quantityButtonWrapper}>
      {isArrayNotEmpty(article.selectableQuantity) && article.selectableQuantity.length ? (
        <QuantityPicker
          className={styles.quantityWrapper}
          isLoading={isLoading[article.id]}
          onValueChange={(value) => handleChange(value)}
          options={article.selectableQuantity}
          value={itemCount}
          id={`${article.id}`}
          key={article.id}
          size={isResponsiveViewport ? 'small' : 'large'}
        />
      ) : (
        <QuantityStepper
          className={cn(
            styles.quantityStepper,
            itemCount <= 1 && styles.quantityStepper_firstButton,
          )}
          isLoading={isLoading[article.id]}
          inputAriaLabel={t('cart.overview.quantityStepper.inputAriaLabel')}
          minusAriaLabel={t('cart.overview.quantityStepper.minusAriaLabel')}
          onValueChange={(value) => handleChange(value)}
          plusAriaLabel={t('cart.overview.quantityStepper.plusAriaLabel')}
          value={itemCount}
          id={`${article.id}`}
          key={article.id}
          isPlusDisabled={itemCount >= article.maxQuantity}
          onKeyDown={handleInputQuantintyDeletion}
          size={isResponsiveViewport ? 'small' : 'large'}
        />
      )}
    </div>
  );
};

export default CartItemQuantityHandler;
